import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Settings,
  Users,
  CreditCard,
  BarChart3,
  Edit,
  Save,
  Building2,
  Shield,
  ShieldCheck,
  Key,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { admin } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import Header from "@/components/layout/Header";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [enterpriseContacts, setEnterpriseContacts] = useState([]);
  
  // Edit dialogs
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [statsRes, plansRes, usersRes, settingsRes, contactsRes] = await Promise.all([
        admin.getStats(),
        admin.getPlans(),
        admin.getUsers(),
        admin.getSettings(),
        admin.getEnterpriseContacts(),
      ]);
      setStats(statsRes.data);
      setPlans(plansRes.data);
      setUsers(usersRes.data.users);
      setAppSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
      setEnterpriseContacts(contactsRes.data);
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load admin data");
      }
    }
    setLoading(false);
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    try {
      await admin.updatePlan(editingPlan.id, editingPlan);
      toast.success("Plan updated successfully");
      setEditingPlan(null);
      loadData();
    } catch {
      toast.error("Failed to update plan");
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await admin.updateSettings(settingsForm);
      toast.success("Settings updated successfully");
      setEditingSettings(false);
      setAppSettings(settingsForm);
    } catch {
      toast.error("Failed to update settings");
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      await admin.toggleAdmin(userId);
      toast.success("Admin status updated");
      loadData();
    } catch {
      toast.error("Failed to update admin status");
    }
  };

  const handleUpdateUserPlan = async (userId, planId) => {
    try {
      await admin.updateUserSubscription(userId, planId);
      toast.success("User subscription updated");
      loadData();
    } catch {
      toast.error("Failed to update subscription");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-slate-500">Loading admin panel...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 bg-slate-900 rounded-lg">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-sm text-slate-500">Manage subscriptions, users, and app settings</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.total_users}</div>
              <div className="text-sm text-slate-500">Total Users</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.total_diagrams}</div>
              <div className="text-sm text-slate-500">Total Diagrams</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.recent_signups_7d}</div>
              <div className="text-sm text-slate-500">Signups (7 days)</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.pending_enterprise_inquiries}</div>
              <div className="text-sm text-slate-500">Enterprise Inquiries</div>
            </div>
          </div>
        )}

        <Tabs defaultValue="plans" className="space-y-6">
          <TabsList>
            <TabsTrigger value="plans" data-testid="admin-plans-tab">
              <CreditCard className="h-4 w-4 mr-2" />
              Subscription Plans
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-settings-tab">
              <Settings className="h-4 w-4 mr-2" />
              App Settings
            </TabsTrigger>
            <TabsTrigger value="enterprise" data-testid="admin-enterprise-tab">
              <Building2 className="h-4 w-4 mr-2" />
              Enterprise
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-audit-tab">
              Audit Log
            </TabsTrigger>
            <TabsTrigger value="stripe" data-testid="admin-stripe-tab">
              Stripe
            </TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Subscription Plans</h2>
                <p className="text-sm text-slate-500">Manage pricing, limits, and features</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Price (Monthly)</TableHead>
                    <TableHead>Price (Annual)</TableHead>
                    <TableHead>Diagrams</TableHead>
                    <TableHead>AI Credits</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-xs text-slate-500">{plan.tagline}</div>
                        </div>
                      </TableCell>
                      <TableCell>${plan.price >= 0 ? plan.price : "Custom"}</TableCell>
                      <TableCell>${plan.price_annual >= 0 ? plan.price_annual : "Custom"}</TableCell>
                      <TableCell>{plan.diagrams_limit >= 0 ? plan.diagrams_limit : "Unlimited"}</TableCell>
                      <TableCell>{plan.ai_credits >= 0 ? plan.ai_credits : "Unlimited"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${plan.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                          {plan.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setEditingPlan({...plan})} data-testid={`edit-plan-${plan.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab users={users} plans={plans} currentUser={user} onUpdatePlan={handleUpdateUserPlan} onToggleAdmin={handleToggleAdmin} onLoadData={loadData} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border border-slate-200 max-w-2xl">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">App Settings</h2>
                  <p className="text-sm text-slate-500">Configure branding and general settings</p>
                </div>
                {!editingSettings ? (
                  <Button variant="outline" size="sm" onClick={() => setEditingSettings(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingSettings(false); setSettingsForm(appSettings); }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleUpdateSettings}>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">App Name</label>
                  <Input
                    value={settingsForm.app_name || ""}
                    onChange={(e) => setSettingsForm({...settingsForm, app_name: e.target.value})}
                    disabled={!editingSettings}
                    className="mt-1"
                    data-testid="app-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Tagline</label>
                  <Input
                    value={settingsForm.app_tagline || ""}
                    onChange={(e) => setSettingsForm({...settingsForm, app_tagline: e.target.value})}
                    disabled={!editingSettings}
                    className="mt-1"
                    data-testid="app-tagline-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Support Email</label>
                  <Input
                    value={settingsForm.support_email || ""}
                    onChange={(e) => setSettingsForm({...settingsForm, support_email: e.target.value})}
                    disabled={!editingSettings}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Enterprise Tab */}
          <TabsContent value="enterprise">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Enterprise Inquiries</h2>
                <p className="text-sm text-slate-500">Contact requests from enterprise prospects</p>
              </div>
              {enterpriseContacts.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No enterprise inquiries yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Team Size</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enterpriseContacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell className="font-medium">{contact.company_name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.team_size}</TableCell>
                        <TableCell className="max-w-xs truncate">{contact.message || "-"}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${contact.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                            {contact.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <AuditLogTab />
          </TabsContent>

          {/* Stripe Settings Tab */}
          <TabsContent value="stripe">
            <StripeSettingsTab />
          </TabsContent>

        </Tabs>
      </div>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Plan: {editingPlan?.name}</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Name</label>
                  <Input
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Order</label>
                  <Input
                    type="number"
                    value={editingPlan.order || 0}
                    onChange={(e) => setEditingPlan({...editingPlan, order: parseInt(e.target.value)})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tagline</label>
                <Input
                  value={editingPlan.tagline || ""}
                  onChange={(e) => setEditingPlan({...editingPlan, tagline: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Price (Monthly)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({...editingPlan, price: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">-1 for custom pricing</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Price (Annual)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price_annual}
                    onChange={(e) => setEditingPlan({...editingPlan, price_annual: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Max Diagrams</label>
                  <Input
                    type="number"
                    value={editingPlan.diagrams_limit}
                    onChange={(e) => setEditingPlan({...editingPlan, diagrams_limit: parseInt(e.target.value)})}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">-1 for unlimited</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">AI Credits</label>
                  <Input
                    type="number"
                    value={editingPlan.ai_credits}
                    onChange={(e) => setEditingPlan({...editingPlan, ai_credits: parseInt(e.target.value)})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Features (one per line)</label>
                <Textarea
                  value={(editingPlan.features || []).join("\n")}
                  onChange={(e) => setEditingPlan({...editingPlan, features: e.target.value.split("\n").filter(f => f.trim())})}
                  rows={5}
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingPlan.is_active}
                    onCheckedChange={(checked) => setEditingPlan({...editingPlan, is_active: checked})}
                  />
                  <label className="text-sm text-slate-700">Active</label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingPlan.is_popular}
                    onCheckedChange={(checked) => setEditingPlan({...editingPlan, is_popular: checked})}
                  />
                  <label className="text-sm text-slate-700">Popular Badge</label>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setEditingPlan(null)}>Cancel</Button>
                <Button onClick={handleUpdatePlan} data-testid="save-plan-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    admin.getAuditLog().then(res => setLogs(res.data)).finally(() => setLoading(false));
  }, []);

  const ACTION_COLORS = {
    update_plan: "bg-blue-100 text-blue-700",
    update_settings: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="audit-log-panel">
      <div className="p-4 border-b border-slate-200">
        <h2 className="font-semibold text-slate-900">Admin Audit Log</h2>
        <p className="text-sm text-slate-500">All admin changes with timestamps</p>
      </div>
      {loading ? (
        <div className="p-8 text-center text-slate-400">Loading...</div>
      ) : logs.length === 0 ? (
        <div className="p-8 text-center text-slate-400">No audit events yet</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map(log => (
              <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                  {new Date(log.timestamp).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm">{log.admin_email}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-700"}`}>
                    {log.action}
                  </span>
                </TableCell>
                <TableCell className="text-sm font-mono text-slate-600">{log.target}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate font-mono">
                  {JSON.stringify(log.details)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function UsersTab({ users, plans, currentUser, onUpdatePlan, onToggleAdmin, onLoadData }) {
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async () => {
    if (!resetUserId || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await admin.resetUserPassword(resetUserId, newPassword);
      toast.success("Password reset successfully");
      setResetUserId(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-4 border-b border-slate-200">
        <h2 className="font-semibold text-slate-900">User Management</h2>
        <p className="text-sm text-slate-500">Manage users, subscriptions, and access</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Diagrams</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell>
                <div>
                  <div className="font-medium text-sm">{u.username || u.email.split('@')[0]}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
              </TableCell>
              <TableCell>
                <Select
                  value={u.subscription?.plan_id || "free"}
                  onValueChange={(val) => onUpdatePlan(u.id, val)}
                >
                  <SelectTrigger className="w-28 h-8 text-xs" data-testid={`user-plan-select-${u.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-sm">{u.diagram_count}</TableCell>
              <TableCell className="text-xs text-slate-500">
                {new Date(u.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Switch
                  checked={u.is_admin || false}
                  onCheckedChange={() => onToggleAdmin(u.id)}
                  disabled={u.id === currentUser?.id}
                  data-testid={`toggle-admin-${u.id}`}
                />
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setResetUserId(u.id)}
                  data-testid={`reset-password-btn-${u.id}`}
                >
                  <Lock className="h-4 w-4 mr-1" />
                  Reset Password
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetUserId} onOpenChange={() => { setResetUserId(null); setNewPassword(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Set a new password for: <strong>{users.find(u => u.id === resetUserId)?.email}</strong>
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">New Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  data-testid="admin-reset-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setResetUserId(null); setNewPassword(""); }}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword} disabled={loading || newPassword.length < 6} data-testid="admin-reset-password-submit">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StripeSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await admin.getStripeSettings();
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load Stripe settings");
    }
    setLoading(false);
  };

  const handleSaveKey = async () => {
    if (!newKey.startsWith("sk_")) {
      toast.error("Invalid Stripe key format (must start with sk_)");
      return;
    }
    setSaving(true);
    try {
      const res = await admin.updateStripeSettings({ stripe_api_key: newKey });
      setSettings(res.data);
      setEditing(false);
      setNewKey("");
      toast.success("Stripe key updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update Stripe key");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400">Loading Stripe settings...</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 max-w-2xl" data-testid="stripe-settings-panel">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <CreditCard className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Stripe Settings</h2>
            <p className="text-sm text-slate-500">Manage your payment gateway configuration</p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">API Key Status</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${settings?.stripe_key_set ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {settings?.stripe_key_set ? "Configured" : "Not Set"}
            </span>
          </div>
          
          {settings?.stripe_key_set && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Mode</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${settings?.stripe_mode === "test" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                  {settings?.stripe_mode === "test" ? "Test Mode" : "Live Mode"}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Current Key</span>
                <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-600">
                  {settings?.stripe_key_masked}
                </code>
              </div>
            </>
          )}
        </div>

        {/* Update Key Form */}
        {!editing ? (
          <Button onClick={() => setEditing(true)} variant="outline" data-testid="edit-stripe-key-btn">
            <Key className="h-4 w-4 mr-2" />
            {settings?.stripe_key_set ? "Update API Key" : "Add API Key"}
          </Button>
        ) : (
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Stripe Secret Key</label>
              <div className="relative">
                <Input
                  type={showKey ? "text" : "password"}
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="sk_test_... or sk_live_..."
                  className="font-mono text-sm"
                  data-testid="stripe-key-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  onClick={() => setShowKey(v => !v)}
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Get your Stripe API keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard</a>
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditing(false); setNewKey(""); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveKey} disabled={saving || !newKey.startsWith("sk_")} data-testid="save-stripe-key-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Key"}
              </Button>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">About Stripe Keys</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Use <code className="bg-blue-100 px-1 rounded">sk_test_</code> keys for testing</li>
            <li>• Use <code className="bg-blue-100 px-1 rounded">sk_live_</code> keys for production</li>
            <li>• Never share your secret keys publicly</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
